import type { Me } from '@feral/shared/client'
import { create } from 'zustand'
import { ApiError, api, whenUnauthorized } from './api.js'

/**
 * Everything held for the signed-in account, cleared when it ends. Each store registers its own
 * reset (queries, the map, chat, the socket, the outbox), so nothing from one account survives into
 * the next on the same device (client review #7, #9).
 */
const endHooks = new Set<() => void>()
export function onSessionEnd(fn: () => void): () => void {
  endHooks.add(fn)
  return () => endHooks.delete(fn)
}
function endLocal() {
  for (const fn of endHooks)
    try {
      fn()
    } catch {}
}

/**
 * A sign-out the server never heard about (offline, an outage). The local account is gone already;
 * this makes the next start finish it before asking who's signed in, so the cookie can't quietly bring
 * the account back after a Quick Exit (client review #10). A Quick Exit's says so (`quick`), so the
 * server still hears it was one when it finally does.
 */
const SIGN_OUT_PENDING = 'fml.signOutPending'
const pendingSignOut = (): 'quick' | 'plain' | null => {
  try {
    const v = localStorage.getItem(SIGN_OUT_PENDING)
    return v === 'quick' ? 'quick' : v ? 'plain' : null
  } catch {
    return null
  }
}
const markSignOut = (on: boolean, quick = false) => {
  try {
    if (on) localStorage.setItem(SIGN_OUT_PENDING, quick ? 'quick' : '1')
    else localStorage.removeItem(SIGN_OUT_PENDING)
  } catch {}
}
async function finishSignOut(quick = false): Promise<boolean> {
  try {
    await api.logout(quick)
  } catch (err) {
    // Unreachable: try again next time. Any answer at all means there's no session to end.
    if (!(err instanceof ApiError) || err.status >= 500) return false
  }
  markSignOut(false)
  return true
}

/**
 * Signed out on purpose (the menu, Account, Quick Exit), as opposed to a session that lapsed. The
 * front door only opens the passkey prompt by itself after a lapse: never right after someone left,
 * and never after a safety exit.
 */
const SIGNED_OUT = 'fml.signedOut'
export function signedOutOnPurpose(): boolean {
  try {
    return localStorage.getItem(SIGNED_OUT) === '1'
  } catch {
    return false
  }
}
const markSignedOut = (on: boolean) => {
  try {
    if (on) localStorage.setItem(SIGNED_OUT, '1')
    else localStorage.removeItem(SIGNED_OUT)
  } catch {}
}

interface SessionState {
  me: Me | null
  loaded: boolean
  load: () => Promise<Me | null>
  set: (me: Me | null) => void
  /** `quick`: a Quick Exit (WF-SAF-04) */
  logout: (quick?: boolean) => Promise<void>
}
export const useSession = create<SessionState>((set, get) => {
  /** A different account than the one held: drop the old one's state before showing the new one. */
  const become = (me: Me | null) => {
    const before = get().me
    if (before && before.id !== me?.id) endLocal()
    if (me) markSignedOut(false)
    set({ me, loaded: true })
  }
  // A request came back 401 while signed in: if /me agrees, the session expired. End it here, as a
  // lapse (not a deliberate sign-out), so the front door shows and can offer the passkey again.
  let checking = false
  whenUnauthorized(() => {
    if (!get().me || checking) return
    checking = true
    api
      .me()
      .then(become)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401 && get().me) become(null)
      })
      .finally(() => {
        checking = false
      })
  })
  return {
    me: null,
    loaded: false,
    async load() {
      const unfinished = pendingSignOut()
      if (unfinished && !(await finishSignOut(unfinished === 'quick'))) {
        become(null)
        return null
      }
      try {
        const me = await api.me()
        become(me)
        return me
      } catch (err) {
        // Only the server saying "not signed in" ends a session. Unreachable (an outage, a restart,
        // a dead connection) keeps whoever was signed in; with nobody yet, it's the front door.
        const held = get().me
        if (held && !(err instanceof ApiError && err.status === 401)) {
          set({ loaded: true })
          return held
        }
        become(null)
        return null
      }
    },
    set: (me) => {
      if (me) markSignOut(false)
      become(me)
    },
    /** Local first, always: a safety exit can't wait on the network. Then the server, if it answers. */
    async logout(quick = false) {
      markSignOut(true, quick)
      markSignedOut(true)
      endLocal()
      set({ me: null, loaded: true })
      await finishSignOut(quick)
    },
  }
})
